/**
 * A tiny hand-rolled reactive layer for the DOM overlay (tech-design §4:
 * "signals … or hand-rolled observable selectors — **no React/Vue framework**").
 *
 * Just enough to bind interface widgets to selector-derived view models: a
 * writable {@link Signal}, a derived {@link computed} selector, and an
 * {@link effect} that re-runs when its dependencies change. Dependency tracking
 * is automatic — reading a signal inside a `computed`/`effect` subscribes to it.
 *
 * Pure TypeScript, no DOM, no dependencies, so it unit-tests headlessly in Node.
 */

/** The reactive computation currently running, for automatic dependency capture. */
let activeObserver: Observer | null = null;

interface Observer {
  /** Re-run the computation. */
  run(): void;
  /** Signals this observer currently depends on (for clean unsubscribe). */
  readonly deps: Set<Set<Observer>>;
}

/** A readable reactive value. Reading `.value` inside a computed/effect subscribes. */
export interface ReadonlySignal<T> {
  readonly value: T;
  /** Read without subscribing the active observer. */
  peek(): T;
  /** Subscribe to changes explicitly; returns an unsubscribe handle. */
  subscribe(listener: (value: T) => void): () => void;
}

/** A writable reactive value. */
export interface Signal<T> extends ReadonlySignal<T> {
  value: T;
  /** Update via a function of the current value. */
  update(fn: (current: T) => T): void;
}

function link(observer: Observer, subscribers: Set<Observer>): void {
  subscribers.add(observer);
  observer.deps.add(subscribers);
}

/** Create a writable signal seeded with `initial`. */
export function signal<T>(initial: T): Signal<T> {
  let current = initial;
  const subscribers = new Set<Observer>();
  const listeners = new Set<(value: T) => void>();

  const notify = (): void => {
    // Copy first: an observer may re-subscribe (mutating the set) as it runs.
    for (const observer of [...subscribers]) {
      observer.run();
    }
    for (const listener of [...listeners]) {
      listener(current);
    }
  };

  return {
    get value(): T {
      if (activeObserver) {
        link(activeObserver, subscribers);
      }
      return current;
    },
    set value(next: T) {
      if (Object.is(next, current)) {
        return;
      }
      current = next;
      notify();
    },
    peek(): T {
      return current;
    },
    update(fn: (current: T) => T): void {
      this.value = fn(current);
    },
    subscribe(listener: (value: T) => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

function cleanup(observer: Observer): void {
  for (const subscribers of observer.deps) {
    subscribers.delete(observer);
  }
  observer.deps.clear();
}

/**
 * Run `fn` immediately and re-run it whenever any signal it reads changes.
 * Returns a disposer that stops further re-runs.
 */
export function effect(fn: () => void): () => void {
  const observer: Observer = {
    deps: new Set(),
    run(): void {
      cleanup(observer);
      const previous = activeObserver;
      activeObserver = observer;
      try {
        fn();
      } finally {
        activeObserver = previous;
      }
    },
  };
  observer.run();
  return () => {
    cleanup(observer);
  };
}

/**
 * A read-only signal derived from other signals. Recomputes lazily-ish: it
 * re-derives whenever a dependency changes and pushes the new value out.
 */
export function computed<T>(fn: () => T): ReadonlySignal<T> {
  const out = signal<T>(undefined as T);
  effect(() => {
    out.value = fn();
  });
  return {
    get value(): T {
      return out.value;
    },
    peek: () => out.peek(),
    subscribe: (listener) => out.subscribe(listener),
  };
}
